import React from "react";
import { useTranslation } from "react-i18next";
import { CINEMATIC_STATES } from "@/lib/decisionGraph";

// Mandatory 2D fallback for clients failing the WebGL2 capability probe. Renders
// the SAME decision-graph model as an accessible, screen-reader-friendly summary.
export default function AccessibleGraphSummary({ model, state, reason }) {
  const { t } = useTranslation();
  const groups = [
    { type: "source", label: t("decisionGraph.accessible.sourceGroup") },
    { type: "agent", label: t("decisionGraph.accessible.agentGroup") },
    { type: "candidate", label: t("decisionGraph.accessible.candidateGroup") },
    { type: "decision", label: t("decisionGraph.accessible.decisionGroup") },
  ];
  const VISIBLE_FROM = { source: 0, agent: 2, candidate: 3, decision: 4 };
  const reasonText = reason ? t("decisionGraph.accessible.reasonSuffix", { reason }) : "";

  return (
    <div className="h-full overflow-y-auto p-6" data-testid="accessible-graph-summary" role="region"
      aria-label={t("decisionGraph.accessible.regionLabel")}>
      <div className="rounded-lg border border-[var(--qn-warning)] bg-[var(--qn-warning-muted)] px-4 py-3 mb-5 text-sm text-[var(--qn-warning)]"
        data-testid="webgl-fallback-notice">
        {t("decisionGraph.accessible.notice", { reason: reasonText })}
      </div>

      <ol className="space-y-1 mb-6" data-testid="accessible-state-track">
        {CINEMATIC_STATES.map((s, i) => (
          <li key={s.key}
            className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm ${
              i === state ? "bg-[var(--qn-primary-muted)] text-[var(--qn-primary)] font-semibold"
                : i < state ? "text-[var(--qn-foreground-muted)]" : "text-[var(--qn-foreground-subtle)]"}`}
            aria-current={i === state ? "step" : undefined}>
            <span className="w-5 text-right tabular-nums">{i + 1}</span>
            <span>{t(`decisionGraph.states.${s.key}.title`)}</span>
            {i === state && <span className="ml-auto text-xs">{t("decisionGraph.accessible.current")}</span>}
          </li>
        ))}
      </ol>

      <div className="grid grid-cols-1 gap-4">
        {groups.map((g) => {
          const nodes = model.nodes.filter((n) => n.type === g.type);
          const revealed = state >= (VISIBLE_FROM[g.type] ?? 0);
          const count = nodes.reduce((s, n) => s + (n.count || 1), 0);
          return (
            <div key={g.type} className="rounded-lg border border-[var(--qn-border)] p-3" data-testid={`accessible-group-${g.type}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-[var(--qn-foreground)]">{g.label}</span>
                <span className="text-xs text-[var(--qn-foreground-subtle)]">
                  {revealed ? t("decisionGraph.accessible.nodeCount", { count }) : t("decisionGraph.accessible.notYet")}
                </span>
              </div>
              {revealed && (
                <div className="flex flex-wrap gap-2">
                  {nodes.map((n) => (
                    <span key={n.id} className="rounded-md border border-[var(--qn-border)] bg-[var(--qn-surface-hover)] px-2 py-1 text-xs font-mono text-[var(--qn-foreground-muted)]">
                      {n.label}
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
